import React, { useState, useRef, useEffect } from 'react';
import { Heart, MessageCircle, Send, Bookmark, MoreHorizontal, Play, Pause } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { Button } from './ui/button';
import { mockReels, mockEntities } from '../mockData';

const ReelPlayer = ({ reel, entity, isActive, onLike, onSave }) => {
  const videoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [showControls, setShowControls] = useState(false);

  useEffect(() => {
    if (isActive && videoRef.current) {
      // For demo purposes, we'll just show a static video poster
      // In real implementation, this would handle video playback
      setIsPlaying(true);
    } else if (videoRef.current) {
      setIsPlaying(false);
    }
  }, [isActive]);

  const togglePlay = () => {
    setIsPlaying(!isPlaying);
    setShowControls(true);
    setTimeout(() => setShowControls(false), 2000);
  };

  const formatNumber = (num) => {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M';
    } else if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K';
    }
    return num.toString();
  };

  return (
    <div className="relative w-full h-full bg-black overflow-hidden">
      {/* Video/Thumbnail */}
      <div 
        className="absolute inset-0 cursor-pointer"
        onClick={togglePlay}
      >
        <img 
          src={reel.thumbnail_url}
          alt={reel.title}
          className="w-full h-full object-cover"
        />
        
        {/* Play/Pause overlay */}
        {showControls && (
          <div className="absolute inset-0 bg-black bg-opacity-20 flex items-center justify-center">
            <div className="bg-white bg-opacity-90 rounded-full p-4">
              {isPlaying ? (
                <Pause className="w-8 h-8 text-gray-800" />
              ) : (
                <Play className="w-8 h-8 text-gray-800 fill-current" />
              )}
            </div>
          </div>
        )}

        {/* Captions */}
        {reel.captions && (
          <div className="absolute bottom-20 left-4 right-16">
            <p className="text-white text-sm leading-relaxed bg-black bg-opacity-50 p-2 rounded">
              {reel.captions}
            </p>
          </div>
        )}
      </div>

      {/* Profile and info */}
      <div className="absolute left-4 bottom-4 right-20">
        <div className="flex items-center gap-3 mb-3">
          <Avatar className="w-8 h-8">
            <AvatarImage src={entity?.avatar_url} alt={entity?.name} />
            <AvatarFallback>{entity?.name?.charAt(0)}</AvatarFallback>
          </Avatar>
          <span className="text-white font-semibold text-sm">{entity?.name}</span>
          <Button variant="outline" size="sm" className="text-white border-white hover:bg-white hover:text-black">
            Follow
          </Button>
        </div>
        
        <h3 className="text-white font-medium text-sm mb-1">{reel.title}</h3>
        
        {reel.source_ref && (
          <div className="inline-block bg-white bg-opacity-20 text-white text-xs px-2 py-1 rounded-full">
            📖 {reel.source_ref}
          </div>
        )}
      </div>

      {/* Right side actions */}
      <div className="absolute right-4 bottom-20 flex flex-col items-center gap-4">
        <Button 
          variant="ghost" 
          size="sm" 
          className={`p-2 text-white hover:bg-white hover:bg-opacity-20 ${
            reel.liked_by_user ? 'text-red-400' : ''
          }`}
          onClick={() => onLike(reel.id, !reel.liked_by_user)}
        >
          <Heart className={`w-6 h-6 ${reel.liked_by_user ? 'fill-current' : ''}`} />
        </Button>
        <span className="text-white text-xs font-medium">{formatNumber(reel.likes)}</span>

        <Button variant="ghost" size="sm" className="p-2 text-white hover:bg-white hover:bg-opacity-20">
          <MessageCircle className="w-6 h-6" />
        </Button>
        <span className="text-white text-xs font-medium">{formatNumber(reel.comments)}</span>

        <Button variant="ghost" size="sm" className="p-2 text-white hover:bg-white hover:bg-opacity-20">
          <Send className="w-6 h-6" />
        </Button>

        <Button 
          variant="ghost" 
          size="sm" 
          className={`p-2 text-white hover:bg-white hover:bg-opacity-20 ${
            reel.saved_by_user ? 'text-blue-400' : ''
          }`}
          onClick={() => onSave(reel.id, !reel.saved_by_user)}
        >
          <Bookmark className={`w-6 h-6 ${reel.saved_by_user ? 'fill-current' : ''}`} />
        </Button>

        <Button variant="ghost" size="sm" className="p-2 text-white hover:bg-white hover:bg-opacity-20">
          <MoreHorizontal className="w-6 h-6" />
        </Button>
      </div>

      {/* View count */}
      <div className="absolute top-4 left-4">
        <span className="bg-black bg-opacity-60 text-white text-xs px-2 py-1 rounded">
          {formatNumber(reel.views)} views
        </span>
      </div>
    </div>
  );
};

const ReelsPage = ({ currentUser, instanceId }) => {
  const [currentReelIndex, setCurrentReelIndex] = useState(0);
  const [reels, setReels] = useState(mockReels.filter(r => r.instance_id === instanceId));

  const handleLike = (reelId, liked) => {
    setReels(prev => prev.map(reel => 
      reel.id === reelId 
        ? { ...reel, liked_by_user: liked, likes: liked ? reel.likes + 1 : reel.likes - 1 }
        : reel
    ));
  };

  const handleSave = (reelId, saved) => {
    setReels(prev => prev.map(reel => 
      reel.id === reelId 
        ? { ...reel, saved_by_user: saved }
        : reel
    ));
  };

  const handleScroll = (e) => {
    const container = e.target;
    const reelHeight = container.clientHeight;
    const scrollTop = container.scrollTop;
    const newIndex = Math.round(scrollTop / reelHeight);
    
    if (newIndex !== currentReelIndex && newIndex < reels.length) {
      setCurrentReelIndex(newIndex);
    }
  };

  if (reels.length === 0) {
    return (
      <div className="h-screen flex items-center justify-center bg-black text-white">
        <div className="text-center">
          <div className="w-16 h-16 bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-4">
            <Play className="w-8 h-8 text-gray-400" />
          </div>
          <h3 className="text-lg font-semibold mb-2">No reels available</h3>
          <p className="text-gray-400 text-sm">Reels will appear here once processing is complete</p>
        </div>
      </div>
    );
  }

  return (
    <div 
      className="h-screen overflow-y-auto snap-y snap-mandatory"
      onScroll={handleScroll}
    >
      {reels.map((reel, index) => {
        const entity = mockEntities[instanceId]?.find(e => e.id === reel.entity_id);
        
        return (
          <div key={reel.id} className="w-full h-screen snap-start relative">
            <ReelPlayer
              reel={reel}
              entity={entity}
              isActive={index === currentReelIndex}
              onLike={handleLike}
              onSave={handleSave}
            />
          </div>
        );
      })}
    </div>
  );
};

export default ReelsPage;